import { useEffect, useRef, useState } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';
import { ToggleField } from '@brighte/form-components';
import { HouseholdType, Period } from '@brighte/validation';
import { RepaymentField } from '@components/RepaymentField';
import { Stack } from '@spark-web/stack';
import { FinancialFieldTypes, HOUSEHOLD } from '@utils';

type Props = Pick<UseFormReturn<HouseholdType>, 'watch' | 'control' | 'setValue'>;

function Rent({ control, watch, setValue }: Props) {
  const { RENT_LABEL } = HOUSEHOLD;

  const RENT_FIELD = 'applicant.expense.rent' as FieldPath<HouseholdType>;
  const MORTGAGE_FIELD = 'applicant.expense.mortgage' as FieldPath<HouseholdType>;

  const [isRentNotApplicable, applicantRentAmount, applicantRentPeriod, isMortgageNotApplicable] = watch([
    `${RENT_FIELD}.isNotApplicable`,
    `${RENT_FIELD}.amount`,
    `${RENT_FIELD}.period`,
    `${MORTGAGE_FIELD}.isNotApplicable`,
  ]);

  const previousMortgageNotApplicable = useRef(isMortgageNotApplicable);
  const [rentToggleKey, setRentToggleKey] = useState(0);

  useEffect(() => {
    if (isMortgageNotApplicable === false) {
      // Mortgage = "Yes" → rent is hidden and doesn't apply, so mark it not-applicable.
      setValue(`${RENT_FIELD}.isNotApplicable` as FieldPath<HouseholdType>, true);
    } else if (isMortgageNotApplicable === true && previousMortgageNotApplicable.current === false) {
      // Switching from "Yes" to "No" reveals the rent question. Clear the not-applicable value the "Yes"
      // branch set so rent starts unselected rather than carrying over as "No". This only fires on the
      // Yes→No transition (not on mount), so a genuinely saved rent answer is preserved when editing.
      setValue(`${RENT_FIELD}.isNotApplicable` as FieldPath<HouseholdType>, undefined as never);
      // Remount the toggle so it re-derives its unselected state — ToggleField only computes "show
      // nothing" at mount, so clearing the value alone would leave it displaying the stale "No".
      setRentToggleKey(key => key + 1);
    }
    previousMortgageNotApplicable.current = isMortgageNotApplicable;
  }, [isMortgageNotApplicable]);

  const handleRentToggle = (value: boolean) => {
    // value is isNotApplicable: true = "No" (not applicable), false = "Yes" (has rent)
    if (value) {
      setValue(`${RENT_FIELD}.amount` as FieldPath<HouseholdType>, 0);
      setValue(`${RENT_FIELD}.period` as FieldPath<HouseholdType>, Period.Monthly);
    }
  };

  return (
    isMortgageNotApplicable && (
      <Stack gap="xxlarge">
        <ToggleField
          key={rentToggleKey}
          name={`${RENT_FIELD}.isNotApplicable` as FieldPath<HouseholdType>}
          label={RENT_LABEL}
          control={control}
          layout="inline"
          onChange={handleRentToggle}
          isInvertedValue
        />
        {!isRentNotApplicable && isRentNotApplicable !== undefined && (
          <RepaymentField
            control={control}
            periodFieldName={`${RENT_FIELD}.period` as FieldPath<HouseholdType>}
            amountFieldName={`${RENT_FIELD}.amount` as FieldPath<HouseholdType>}
            periodValue={applicantRentPeriod}
            amountValue={applicantRentAmount}
            financialFieldType={FinancialFieldTypes.RENT}
            heading="Rent payments"
          />
        )}
      </Stack>
    )
  );
}

export default Rent;
